import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { officeAPI } from '../../services/api';
import { format, startOfISOWeek, startOfMonth } from 'date-fns';
const fmt = 'yyyy-MM-dd';
const fmtDate = (d) => d ? format(new Date(d), 'yyyy/MM/dd') : '—';

const PERIODS = [
  { label: 'اليوم', value: 'today' },
  { label: 'هذا الأسبوع', value: 'week' },
  { label: 'هذا الشهر', value: 'month' },
  { label: 'مخصص', value: 'custom' },
];

function getPeriodDates(period) {
  const today = format(new Date(), fmt);
  switch (period) {
    case 'today': return { from: today, to: today };
    case 'week':  return { from: format(startOfISOWeek(new Date()), fmt), to: today };
    case 'month': return { from: format(startOfMonth(new Date()), fmt), to: today };
    default:      return { from: '', to: '' };
  }
}

export default function OfficeCommissionPage() {
  const [period, setPeriod]     = useState('month');
  const [from, setFrom]         = useState('');
  const [to, setTo]             = useState('');
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);

  const fetchCommission = async (p, f, t) => {
    const dates = p === 'custom' ? { from: f, to: t } : getPeriodDates(p);
    if (!dates.from || !dates.to) return;
    setLoading(true);
    try {
      const res = await officeAPI.getCommission(dates);
      const d = res.data.data;
      setData({
        totalBookings:  d.summary?.totalBookings  ?? 0,
        totalSeats:     d.summary?.totalSeats     ?? 0,
        totalRevenue:   d.summary?.totalRevenue   ?? 0,
        totalCommission: d.summary?.totalCommission ?? 0,
        byDate: d.byDate || [],
      });
    } catch { toast.error('خطأ في تحميل العمولات'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (period !== 'custom') fetchCommission(period, '', '');
  }, [period]);

  const handleCustomSearch = () => {
    if (!from || !to) return toast.warning('حدد التاريخين');
    fetchCommission('custom', from, to);
  };

  return (
    <div dir="rtl" className="p-4 md:p-6 max-w-3xl">
      <h1 className="text-xl font-bold text-gray-900 mb-5">تقرير العمولات</h1>

      {/* Period selector */}
      <div className="flex flex-wrap gap-2 mb-5">
        {PERIODS.map((p) => (
          <button
            key={p.value}
            onClick={() => setPeriod(p.value)}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${period === p.value ? 'bg-blue-600 text-white shadow-sm' : 'bg-white border text-gray-600 hover:border-blue-300'}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Custom date range */}
      {period === 'custom' && (
        <div className="bg-white rounded-2xl border p-4 mb-5 flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-500 mb-1">من</label>
            <input type="date" className="border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">إلى</label>
            <input type="date" className="border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <button onClick={handleCustomSearch} className="bg-blue-600 text-white px-5 py-2 rounded-xl text-sm font-bold hover:bg-blue-700 transition">
            عرض
          </button>
        </div>
      )}

      {loading && <div className="text-center py-12 text-gray-400">جاري التحميل...</div>}

      {!loading && data && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            {[
              { label: 'إجمالي الحجوزات', value: data.totalBookings, suffix: '', color: 'text-blue-700' },
              { label: 'إجمالي المقاعد', value: data.totalSeats, suffix: '', color: 'text-indigo-700' },
              { label: 'إجمالي المبيعات', value: parseFloat(data.totalRevenue || 0).toLocaleString(), suffix: 'ج.س', color: 'text-gray-900' },
              { label: 'عمولة المكتب', value: parseFloat(data.totalCommission || 0).toLocaleString(), suffix: 'ج.س', color: 'text-emerald-700' },
            ].map((card) => (
              <div key={card.label} className="bg-white rounded-2xl border p-4 shadow-sm">
                <p className="text-xs text-gray-500 font-medium">{card.label}</p>
                <p className={`text-2xl font-bold mt-2 ${card.color} tabular-nums`}>{card.value}</p>
                {card.suffix && <p className="text-xs text-gray-400">{card.suffix}</p>}
              </div>
            ))}
          </div>

          {/* Daily breakdown table */}
          {data.byDate && data.byDate.length > 0 && (
            <div className="bg-white rounded-2xl border overflow-hidden mb-6">
              <div className="px-5 py-4 border-b">
                <h2 className="font-bold text-gray-800">تفصيل يومي</h2>
              </div>
              {/* Desktop/tablet table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-right font-semibold text-gray-600">التاريخ</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-600">حجوزات</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-600">مقاعد</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-600">مبيعات</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-600">عمولة</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {data.byDate.map((row) => (
                      <tr key={row.date} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-800">{fmtDate(row.date)}</td>
                        <td className="px-4 py-3 tabular-nums text-gray-700">{row.bookings}</td>
                        <td className="px-4 py-3 tabular-nums text-gray-700">{row.seats}</td>
                        <td className="px-4 py-3 tabular-nums text-gray-800 font-medium">{parseFloat(row.revenue || 0).toLocaleString()}</td>
                        <td className="px-4 py-3 tabular-nums text-emerald-700 font-bold">{parseFloat(row.commission || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50 border-t font-bold">
                    <tr>
                      <td className="px-4 py-3 text-gray-700">الإجمالي</td>
                      <td className="px-4 py-3 tabular-nums">{data.totalBookings}</td>
                      <td className="px-4 py-3 tabular-nums">{data.totalSeats}</td>
                      <td className="px-4 py-3 tabular-nums">{parseFloat(data.totalRevenue || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 tabular-nums text-emerald-700">{parseFloat(data.totalCommission || 0).toLocaleString()}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Mobile card list — same data as the table above. */}
              <div className="md:hidden divide-y">
                {data.byDate.map((row) => (
                  <div key={row.date} className="p-4 space-y-1.5">
                    <p className="font-medium text-gray-800 text-sm">{fmtDate(row.date)}</p>
                    <div className="flex items-center justify-between text-xs text-gray-600">
                      <span>{row.bookings} حجز · {row.seats} مقعد</span>
                      <span className="tabular-nums text-gray-800 font-medium">{parseFloat(row.revenue || 0).toLocaleString()} ج.س</span>
                    </div>
                    <p className="text-xs text-emerald-700 font-bold tabular-nums">عمولة: {parseFloat(row.commission || 0).toLocaleString()} ج.س</p>
                  </div>
                ))}
                <div className="p-4 bg-gray-50 space-y-1.5">
                  <p className="font-bold text-gray-700 text-sm">الإجمالي</p>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-700 font-semibold">{data.totalBookings} حجز · {data.totalSeats} مقعد</span>
                    <span className="tabular-nums font-bold text-gray-900">{parseFloat(data.totalRevenue || 0).toLocaleString()} ج.س</span>
                  </div>
                  <p className="text-xs text-emerald-700 font-bold tabular-nums">عمولة: {parseFloat(data.totalCommission || 0).toLocaleString()} ج.س</p>
                </div>
              </div>
            </div>
          )}

          {data.totalBookings === 0 && (
            <div className="bg-white rounded-2xl border p-10 text-center text-gray-400">
              لا توجد حجوزات في هذه الفترة
            </div>
          )}
        </>
      )}
    </div>
  );
}
